// Role-based access control utilities

export interface UserPermissions {
  /** Permission codes for Access Management (e.g. role.create, user.read) */
  permissionCodes?: string[];
  academyAccess?: {
    hasAccess: boolean;
    academyIds: string[];
  };
  tournamentAccess?: {
    hasAccess: boolean;
    tournamentIds: string[];
  };
  coachingCenterAccess?: {
    hasAccess: boolean;
    coachingCenterIds: string[];
  };
  clubAccess?: {
    hasAccess: boolean;
    clubIds: string[];
  };
}

export interface User {
  id?: string;
  fullName?: string;
  email?: string;
  subRole?: string | null;
  permissions?: UserPermissions;
}

const getPermissionCodes = (user: User | null | undefined): string[] => {
  if (!user || !user.permissions) return [];
  const permissions = user.permissions;
  const codes = Array.isArray(permissions)
    ? (permissions as unknown as string[])
    : (permissions as UserPermissions).permissionCodes;
  if (!codes || !Array.isArray(codes)) return [];
  return codes;
};

// Check if user is a club manager (permission-based)
export const isClubManager = (user: User): boolean => {
  if (!user) return false;
  // Club managers should have BOTH read and manage on clubs,
  // and should not be platform admins.
  const hasClubPerms = hasAllPermissions(user, ['club.read', 'club.manage']);
  return hasClubPerms && !isSuperAdmin(user);
};

// Check if user is a super admin (platform admin)
export const isSuperAdmin = (user: User): boolean => {
  if (!user) return false;
  const codes = getPermissionCodes(user);
  return codes.includes('platform.admin');
};

// Operations admin is now an alias for platform admin
export const isOperationsAdmin = (user: User): boolean => {
  return isSuperAdmin(user);
};

// Legacy function for backward compatibility
export const isAdmin = (user: User): boolean => {
  return isSuperAdmin(user);
};

// Check if user has access to a specific module (permission-based)
export const hasModuleAccess = (user: User, moduleName: string): boolean => {
  if (!user) return false;

  const moduleKey = moduleName.toLowerCase();

  // Modules that should remain club-manager only, even for platform admins
  const clubOnlyModules = new Set([
    'myclub',
    'team-information',
    'player-information',
    'club-information',
    'paymenttransactions',
    'dues',
    'transactions',
  ]);

  // Platform admin should NOT see club-only modules
  if (isSuperAdmin(user) && clubOnlyModules.has(moduleKey)) {
    return false;
  }

  // Platform admin has access to all other modules
  if (isSuperAdmin(user)) {
    return true;
  }

  const codes = getPermissionCodes(user);

  // Special case: sidebar \"Payment & Transactions\" (and its children) should
  // only be visible when the user has the explicit tournament.payments.view
  // permission, even if they have tournament.admin / tournament.manage.
  // For global read-only tournament admins (tournament.read) and generic
  // tournament.view, also allow read-only view access.
  if (['paymenttransactions', 'dues', 'transactions'].includes(moduleKey)) {
    return (
      codes.includes('tournament.payments.view') ||
      codes.includes('tournament.view') ||
      codes.includes('tournament.read')
    );
  }

  // Map module IDs to required permissions
  const modulePermissionMap: Record<string, string[]> = {
    // Dashboard
    dashboard: [],

    // Club / My Club
    myclub: ['club.read', 'club.manage'],
    'team-information': ['club.read', 'club.manage'],
    'player-information': ['club.read', 'club.manage'],
    'club-information': ['club.read', 'club.manage'],

    // Tournaments top-level menu
    tournaments: [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.registrations.view',
      'tournament.registrations.manage',
      'tournament.dashboard.view',
      'tournament.registration_details.manage',
      'club.read',
      'club.manage',
      'tournament.players.view',
      'tournament.players.manage',
      'tournament.payments.view',
      'tournament.tournaments.view',
      'tournament.tournaments.manage',
      'tournament.fixtures.manage',
    ],
    paymenttransactions: [
      'tournament.payments.view',
      'tournament.view',
      'tournament.read',
    ],
    dues: [
      'tournament.payments.view',
      'tournament.view',
      'tournament.read',
    ],
    transactions: [
      'tournament.payments.view',
      'tournament.view',
      'tournament.read',
    ],

    // Tournament admin modules
    // Tournament registrations group
    // - registrations.* control registration dashboards and details
    // - club.* allow access to "All Clubs" under this group
    'tournament-registrations': [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.registrations.view',
      'tournament.registrations.manage',
      'club.read',
      'club.manage',
      'tournament.dashboard.view',
      'tournament.registration_details.manage',
      'tournament.players.view',
      'tournament.players.manage',
      'tournament.payments.view',
      'tournament.tournaments.view',
    ],
    'tournament-registrations-dashboard': [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.registrations.view',
      'tournament.registrations.manage',
      'tournament.dashboard.view',
    ],
    'manage-registration-details': [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.registrations.view',
      'tournament.registrations.manage',
      'tournament.registration_details.manage',
    ],
    registeredtournaments: [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.registrations.view',
      'tournament.registrations.manage',
      'tournament.registration_details.manage',
    ],
    allclubs: [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.registrations.view',
      'tournament.registrations.manage',
      'club.read',
      'club.manage',
    ],
    manageplayers: [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.registrations.view',
      'tournament.registrations.manage',
      'tournament.players.view',
      'tournament.players.manage',
    ],
    superadminpayments: [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.registrations.view',
      'tournament.registrations.manage',
      'tournament.payments.view',
    ],
    managetournaments: [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.registrations.view',
      'tournament.registrations.manage',
      'tournament.tournaments.view',
      'tournament.tournaments.manage',
    ],
    'tournament-management': [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.tournaments.view',
      'tournament.tournaments.manage',
      'tournament.fixtures.manage',
    ],
    sportsmanagement: [
      'sports_management.view',
      'sports_management.manage',
    ],
    managetournamentsfixtures: [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.tournaments.view',
      'tournament.tournaments.manage',
      'tournament.fixtures.manage',
    ],
    fixtures: [
      'tournament.admin',
      'tournament.manage',
      'tournament.view',
      'tournament.read',
      'tournament.fixtures.manage',
    ],

    // App & Website (platform-admin only; no specific perms)
    'app-website': [],
    weeklyreports: [],
    bannermanagement: [],
    blogdetails: [],
    categories: [],
    blogs: [],
    newsarticles: [],
    community: [],

    // Shop services
    shopservices: [
      'shop.view',
      'shop.admin',
      'shop.seller.view',
      'shop.seller.manage',
      'shop.product.view',
      'shop.product.manage',
      'shop.order.view',
      'shop.order.manage',
    ],
    sellers: ['shop.view', 'shop.admin', 'shop.seller.view', 'shop.seller.manage'],
    products: [
      'shop.view',
      'shop.admin',
      'shop.product.view',
      'shop.product.manage',
    ],
    orders: ['shop.view', 'shop.admin', 'shop.order.view', 'shop.order.manage'],

    // Promos
    // - promo.view: read-only access to all Promos sections
    // - promo.admin: full access to all Promos sections
    // - promo.campaigns.*: campaigns-only access
    // - promo.audit_logs.view: audit-logs-only access
    promos: [
      'promo.view',
      'promo.admin',
      'promo.campaigns.view',
      'promo.campaigns.manage',
      'promo.audit_logs.view',
    ],
    'all-promos': [
      'promo.view',
      'promo.admin',
    ],
    campaigns: [
      'promo.view',
      'promo.admin',
      'promo.campaigns.view',
      'promo.campaigns.manage',
    ],
    'audit-logs': [
      'promo.view',
      'promo.admin',
      'promo.audit_logs.view',
    ],

    // Feature management
    // - feature_management.view: can see all Feature Management sections in read-only mode
    // - feature_management.admin: overrides all feature_management.* permissions (handled in hasPermission)
    // - feature_management.features.* and feature_management.feature_toggles.*: work independently as sub-permissions
    featuremanagement: [
      'feature_management.view',
      'feature_management.admin',
      'feature_management.features.view',
      'feature_management.features.manage',
      'feature_management.feature_toggles.view',
      'feature_management.feature_toggles.manage',
    ],
    features: [
      'feature_management.view',
      'feature_management.admin',
      'feature_management.features.view',
      'feature_management.features.manage',
    ],
    featuretoggles: [
      'feature_management.view',
      'feature_management.admin',
      'feature_management.feature_toggles.view',
      'feature_management.feature_toggles.manage',
    ],

    // Access management
    accessmanagement: [
      'access_management.view',
      'access_management.admin',
      'access_management.users.view',
      'access_management.users.manage',
      'access_management.roles.view',
      'access_management.roles.manage',
      'access_management.permissions.view',
      'access_management.permissions.manage',
      'access_management.audit_logs.view',
    ],
    users: [
      'access_management.view',
      'access_management.admin',
      'access_management.users.view',
      'access_management.users.manage',
    ],
    roles: [
      'access_management.view',
      'access_management.admin',
      'access_management.roles.view',
      'access_management.roles.manage',
    ],
    permissions: [
      'access_management.view',
      'access_management.admin',
      'access_management.permissions.view',
      'access_management.permissions.manage',
    ],
    auditlogs: [
      'access_management.view',
      'access_management.admin',
      'access_management.audit_logs.view',
    ],

    // Academy Management
    academymanagement: [
      'academy_management.admin',
      'academy_management.view',
      'academy_management.enrollments.view',
      'academy_management.enrollments.manage',
      'academy_management.trials.manage',
    ],
    academyenrolments: [
      'academy_management.admin',
      'academy_management.view',
      'academy_management.enrollments.view',
      'academy_management.enrollments.manage',
    ],
    academytrialadd: [
      'academy_management.admin',
      'academy_management.enrollments.manage',
      'academy_management.trials.manage',
    ],
    academycourseassignments: [
      'academy_management.admin',
      'academy_management.view',
      'academy_management.enrollments.view',
      'academy_management.enrollments.manage',
    ],

    // Ads module
    // - ads.view: read-only access to Ads listing screens
    // - ads.manage/admin: write access to Ads actions
    ads: ['ads.view', 'ads.admin', 'ads.manage'],

    // Player Vault module — permission codes to be defined by backend roles.
    // Empty arrays restrict to platform admins until real codes are assigned.
    playervault: [],
    playervaultclaimrequests: [],
  };

  const requiredPermissions = modulePermissionMap[moduleKey];

  // If module not configured, deny access by default
  if (requiredPermissions === undefined) {
    return false;
  }

  // Modules with empty required permissions are restricted to platform admins (already handled above)
  if (requiredPermissions.length === 0) {
    return false;
  }

  return hasAnyPermission(user, requiredPermissions);
};

// Check if user has access to specific club data
export const hasClubAccess = (user: User, clubId: string): boolean => {
  if (!user || !user.permissions) return false;
  
  const clubAccess = user.permissions.clubAccess;
  if (!clubAccess || !clubAccess.hasAccess) return false;
  
  return clubAccess.clubIds.includes(clubId);
};

// Get user's accessible club IDs
export const getUserClubIds = (user: User): string[] => {
  if (!user || !user.permissions) return [];
  
  const clubAccess = user.permissions.clubAccess;
  if (!clubAccess || !clubAccess.hasAccess) return [];
  
  return clubAccess.clubIds || [];
};

// Check if user has tournament access
export const hasTournamentAccess = (user: User, tournamentId?: string): boolean => {
  if (!user || !user.permissions) return false;
  if (isSuperAdmin(user)) return true;
  
  const tournamentAccess = user.permissions.tournamentAccess;
  if (!tournamentAccess || !tournamentAccess.hasAccess) return false;
  
  if (tournamentId) {
    return tournamentAccess.tournamentIds.includes(tournamentId);
  }
  
  return true; // Has access to tournaments in general
};

// Get user's accessible tournament IDs
export const getUserTournamentIds = (user: User): string[] => {
  if (!user || !user.permissions) return [];
  
  const tournamentAccess = user.permissions.tournamentAccess;
  if (!tournamentAccess || !tournamentAccess.hasAccess) return [];
  
  return tournamentAccess.tournamentIds || [];
};

// Filter menu items based on user role
export const filterMenuItems = (menuItems: any[], user: User): any[] => {
  if (!user) return menuItems;
  
  return menuItems.filter(item => {
    if (item.isHeader) return true;
    
    // Check if user has access to this menu item
    if (!hasModuleAccess(user, item.id)) {
      return false;
    }
    
    // Recursively filter sub-items if they exist
    if (item.subItems && Array.isArray(item.subItems)) {
      item.subItems = item.subItems.filter((subItem: any) => {
        // Check if user has access to sub-item
        if (!hasModuleAccess(user, subItem.id)) {
          return false;
        }
        
        // Recursively filter nested sub-items (e.g., blogdetails -> categories/blogs)
        if (subItem.subItems && Array.isArray(subItem.subItems)) {
          subItem.subItems = subItem.subItems.filter((nestedSubItem: any) => {
            return hasModuleAccess(user, nestedSubItem.id);
          });
        }
        
        return true;
      });
    }
    
    return true;
  });
};

// Get user role display name (permission-based with legacy fallback)
export const getUserRoleDisplayName = (user: User): string => {
  if (!user) return 'User';

  if (isSuperAdmin(user)) {
    return 'Super Admin';
  }

  if (isClubManager(user)) {
    return 'Club Manager';
  }

  // Legacy fallback to subRole if present
  if (user.subRole) {
    switch (user.subRole) {
      case 'club-manager':
        return 'Club Manager';
      case 'super-admin':
        return 'Super Admin';
      case 'operations-admin':
        return 'Operations Admin';
      default:
        return user.subRole.charAt(0).toUpperCase() + user.subRole.slice(1);
    }
  }

  return 'User';
};

/**
 * Check if user has a specific permission code (e.g. role.create, user.read).
 * Used for Access Management and granular action permissions.
 */
export const hasPermission = (
  user: User | null | undefined,
  permissionCode: string
): boolean => {
  if (!user) return false;
  const codes = getPermissionCodes(user);

  // Platform admin has access to all permissions
  if (codes.includes('platform.admin')) {
    return true;
  }

  // Namespace admin (e.g. feature_management.admin, tournament.admin) has access
  // to all permissions in that namespace.
  const namespace = permissionCode.split('.')[0];
  if (namespace && codes.includes(`${namespace}.admin`)) {
    return true;
  }

  return codes.includes(permissionCode);
};

/**
 * Check if user has write/manage access for tournament modules.
 * This is true when the user has tournament.admin, tournament.manage, or any
 * of the provided manage-level permissions for a specific module.
 */
export const hasTournamentWriteAccess = (
  user: User | null | undefined,
  specificManagePermissions: string[]
): boolean => {
  if (!user) return false;
  if (hasAnyPermission(user, ['tournament.admin', 'tournament.manage'])) {
    return true;
  }
  if (specificManagePermissions.length === 0) {
    return false;
  }
  return hasAnyPermission(user, specificManagePermissions);
};

/**
 * Check if user has any of the given permission codes.
 */
export const hasAnyPermission = (
  user: User | null | undefined,
  permissionCodes: string[]
): boolean => {
  return permissionCodes.some((code) => hasPermission(user, code));
};

/**
 * Check if user has all of the given permission codes.
 */
export const hasAllPermissions = (
  user: User | null | undefined,
  permissionCodes: string[]
): boolean => {
  return permissionCodes.every((code) => hasPermission(user, code));
};

// Check if user can perform specific actions
export const canPerformAction = (user: User, action: string, resourceId?: string): boolean => {
  if (!user) return false;
  
  switch (action) {
    case 'add_team':
    case 'add_player':
    case 'register_team':
      return (
        hasAnyPermission(user, [
          'club.manage',
          'tournament.players.manage',
          'tournament.registrations.manage',
        ]) || isSuperAdmin(user)
      );
    case 'manage_tournaments':
      return (
        hasAnyPermission(user, [
          'tournament.tournaments.manage',
          'tournament.fixtures.manage',
        ]) || isSuperAdmin(user)
      );
    case 'view_all_data':
      return isSuperAdmin(user);
    case 'manage_all_clubs':
      return isSuperAdmin(user);
    case 'view_all_clubs':
      return (
        hasAnyPermission(user, ['club.read', 'club.manage']) || isSuperAdmin(user)
      );
    case 'manage_system':
      return isSuperAdmin(user);
    case 'manage_fixtures':
      return (
        hasPermission(user, 'tournament.fixtures.manage') || isSuperAdmin(user)
      );
    default:
      return true;
  }
}; 